/**
 * Rate limit status card component.
 * Displays current rate limit usage and status.
 */

// React Imports
import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip,
  Alert,
} from '@mui/material';
import { Speed as SpeedIcon, Warning as WarningIcon } from '@mui/icons-material';
import type { RateLimitStatus } from '../../types/connector';

export interface RateLimitStatusCardProps {
  status: RateLimitStatus;
  showDetails?: boolean;
}

/**
 * Rate limit status card component.
 */
export const RateLimitStatusCard = ({ status, showDetails = true }: RateLimitStatusCardProps) => {
  const [timeUntilReset, setTimeUntilReset] = useState<string>('');

  useEffect(() => {
    if (status.resetAt) {
      const updateTimer = () => {
        const resetDate = new Date(status.resetAt!);
        const now = new Date();
        const diff = resetDate.getTime() - now.getTime();

        if (diff > 0) {
          const minutes = Math.floor(diff / 60000);
          const seconds = Math.floor((diff % 60000) / 1000);
          setTimeUntilReset(`${minutes}m ${seconds}s`);
        } else {
          setTimeUntilReset('Resetting...');
        }
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    }
  }, [status.resetAt]);

  const getUsagePercentage = (current: number, limit?: number) => {
    if (!limit || limit === 0) return 0;
    return Math.min((current / limit) * 100, 100);
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 75) return 'warning';
    return 'success';
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SpeedIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Rate Limit Status
          </Typography>
          {status.isExceeded && (
            <Chip
              icon={<WarningIcon />}
              label="Exceeded"
              color="error"
              size="small"
              sx={{ ml: 'auto' }}
            />
          )}
        </Box>

        {status.isExceeded && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Rate limit exceeded. Please wait until {status.resetAt ? new Date(status.resetAt).toLocaleTimeString() : 'reset'}.
          </Alert>
        )}

        {showDetails && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Per Minute */}
            {status.rateLimitPerMinute !== undefined && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Per Minute
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {status.currentMinuteUsage} / {status.rateLimitPerMinute}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={getUsagePercentage(status.currentMinuteUsage, status.rateLimitPerMinute)}
                  color={getProgressColor(getUsagePercentage(status.currentMinuteUsage, status.rateLimitPerMinute))}
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>
            )}

            {/* Per Hour */}
            {status.rateLimitPerHour !== undefined && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Per Hour
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {status.currentHourUsage} / {status.rateLimitPerHour}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={getUsagePercentage(status.currentHourUsage, status.rateLimitPerHour)}
                  color={getProgressColor(getUsagePercentage(status.currentHourUsage, status.rateLimitPerHour))}
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>
            )}

            {/* Per Day */}
            {status.rateLimitPerDay !== undefined && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Per Day
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {status.currentDayUsage} / {status.rateLimitPerDay}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={getUsagePercentage(status.currentDayUsage, status.rateLimitPerDay)}
                  color={getProgressColor(getUsagePercentage(status.currentDayUsage, status.rateLimitPerDay))}
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>
            )}

            {timeUntilReset && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Resets in: {timeUntilReset}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
